import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * The search field an index page offers beside its alphabet rail: a text input
 * capped to a readable measure, with a control that empties it once there is
 * something to empty.
 *
 *   <IndexSearchField>
 *     <Input value={value} onChange={onChange} label={label} />
 *     {value.length > 0 && (
 *       <IndexSearchFieldClear onClick={clear} aria-label={clearLabel}>
 *         <X className="w-4" />
 *       </IndexSearchFieldClear>
 *     )}
 *   </IndexSearchField>
 *
 * The INPUT is not a part here. It is `@vs-shop/ui`'s primitive, and what it
 * holds — the value, the debounce, the label — is tenant state and tenant words;
 * this section contributes the box that positions the pair and the control that
 * clears it.
 *
 * Whether the clear control is composed at all is the tenant's, from its own
 * value: an absent part yields no place, and the field's box is unchanged
 * either way.
 *
 * Server-first: both parts are plain server components. A tenant whose clear
 * control needs a handler declares that leaf itself.
 */

/**
 * The field's box. `relative` is structural — the clear control hangs inside it
 * — and the measure is the section's: past roughly 744px a single-line search
 * field stops reading as one.
 */
const indexSearchFieldClasses = 'relative w-full max-w-[744px]';

export type IndexSearchFieldProps = ComponentProps<'div'>;

/** The search field's box. */
export const IndexSearchField = ({ className, ...props }: IndexSearchFieldProps) => (
  <div
    data-slot="index-search-field"
    className={clsxm(indexSearchFieldClasses, className)}
    {...props}
  />
);

/**
 * The control that empties the field, hung at its trailing edge.
 *
 * Hung out of flow and centred on the field's own height, which is the part of
 * the placement that is the same everywhere. HOW BIG it is, and how far in from
 * the trailing edge, are the caller's — its two callers here are two shipped
 * fields at two sizes, and a part that stated one would resize the other. What
 * it DRAWS arrives as children — the mark is the tenant's icon set.
 */
const indexSearchFieldClearClasses = 'absolute top-1/2 -translate-y-1/2 cursor-pointer';

export type IndexSearchFieldClearProps = ComponentProps<'button'>;

/**
 * The clear control. What it is called arrives as `aria-label` on the native
 * prop spread, because "clear the search" is a tenant's words.
 */
export const IndexSearchFieldClear = ({ className, ...props }: IndexSearchFieldClearProps) => (
  <button
    type="button"
    data-slot="index-search-field-clear"
    className={clsxm(indexSearchFieldClearClasses, className)}
    {...props}
  />
);
